
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import ChatInterface from '@/components/chat/ChatInterface';
import { ChatbotData } from '@/components/chat/chatTypes';
import { supabase } from '@/integrations/supabase/client';

const Chat = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [chatbot, setChatbot] = useState<ChatbotData | null>(null);
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Check if user is authenticated
    const userToken = localStorage.getItem('userToken');
    setIsAuthenticated(!!userToken);
    
    if (!userToken) {
      navigate('/login');
    }
  }, [navigate]);
  
  useEffect(() => {
    const fetchAgentData = async () => {
      if (!chatbotId) return;
      
      setIsLoading(true);
      
      try {
        // Fetch agent details
        const { data: agentData, error: agentError } = await supabase
          .from('agents')
          .select('*')
          .eq('id', chatbotId)
          .single();
        
        if (agentError) {
          throw agentError;
        }
        
        if (!agentData) {
          navigate('/explore');
          return;
        }
        
        // Format agent data
        const formattedChatbot: ChatbotData = {
          id: agentData.id,
          name: agentData.name,
          description: agentData.description,
          tags: agentData.tags,
          avatarUrl: agentData.avatar_url,
          conversations: agentData.conversations
        };
        
        setChatbot(formattedChatbot);
        
        // Fetch system prompt
        const { data: promptData, error: promptError } = await supabase
          .from('system_prompts')
          .select('content')
          .eq('agent_id', chatbotId)
          .single();
        
        if (promptError) {
          console.error('Error fetching system prompt:', promptError);
          // We can still show the chat without the system prompt
        } else if (promptData) {
          setSystemPrompt(promptData.content);
        }
        
        // Update conversation count
        const { error: updateError } = await supabase
          .from('agents')
          .update({ conversations: agentData.conversations + 1 })
          .eq('id', chatbotId);
        
        if (updateError) {
          console.error('Error updating conversation count:', updateError);
        }
      } catch (error) {
        console.error('Error fetching agent data:', error);
        navigate('/explore');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAgentData();
  }, [chatbotId, navigate]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  if (!chatbot || !isAuthenticated) {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 mb-10">
        <div className="grid md:grid-cols-[1fr_2fr] gap-6">
          <div>
            <div
              className="h-48 w-full bg-cover bg-center rounded-xl shadow-sm mb-4"
              style={{ backgroundImage: `url(${chatbot.avatarUrl})` }}
            />
            <h1 className="text-2xl font-display font-bold mb-2">{chatbot.name}</h1>
            <p className="text-muted-foreground mb-4">{chatbot.description}</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {chatbot.tags.map((tag: string) => (
                <span key={tag} className="bg-secondary px-3 py-1 rounded-full text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          <ChatInterface 
            chatbotId={chatbot.id} 
            chatbotName={chatbot.name} 
            chatbotAvatar={chatbot.avatarUrl}
            systemPrompt={systemPrompt}
          />
        </div>
      </div>
    </Layout>
  );
};

export default Chat;
